import React, {useEffect, useRef, useState} from 'react';
import { Sparkles, Plus, Trash2, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import {generateProject} from "../../api/ai/ai.api";

const AIAutoSetupPage = () => {
    // 상태 관리: 'input' | 'processing' | 'completed'
    const [status, setStatus] = useState('input');

    // 폼 데이터 상태
    const [eventName, setEventName] = useState('');
    const [coupons, setCoupons] = useState(['']); // 기본으로 1개의 쿠폰 입력창 생성
    const [prompt, setPrompt] = useState('');

    // 로딩 멘트 애니메이션을 위한 상태
    const [loadingMessage, setLoadingMessage] = useState('AI가 요구사항을 분석하고 있습니다...');

    const intervalRef = useRef(null);

    const loadingMessages = [
        "쿠폰을 분석하고 있습니다...",
        "쿠폰 이미지를 생성 중입니다...",
        "이벤트를 생성 중입니다...",
        "이벤트 게임을 생성하고 있습니다...",
        "이벤트 이미지를 분석 중입니다...",
        "진입점을 확인하고 있습니다...",
        "프로젝트를 생성 중입니다...",
        "쿠폰과 이벤트를 연결하고 있습니다..."
    ];

    // 쿠폰 입력값 변경 핸들러
    const handleCouponChange = (index, value) => {
        const newCoupons = [...coupons];
        newCoupons[index] = value;
        setCoupons(newCoupons);
    };

    // 쿠폰 필드 추가
    const addCouponField = () => {
        setCoupons([...coupons, '']);
    };

    // 쿠폰 필드 삭제
    const removeCouponField = (index) => {
        const newCoupons = coupons.filter((_, i) => i !== index);
        setCoupons(newCoupons);
    };

    // 생성하기 버튼 클릭 핸들러
    const handleGenerate = () => {
        if (!eventName.trim()) {
            alert('이벤트명을 입력해주세요.');
            return;
        }

        setStatus('processing');
        setLoadingMessage("AI가 분석을 시작합니다...");

        intervalRef.current = setInterval(() => {
            const randomIndex = Math.floor(Math.random() * loadingMessages.length);
            setLoadingMessage(loadingMessages[randomIndex]);
        }, 10000);

        generateProject(eventName, coupons, prompt).then(() => {
            clearInterval(intervalRef.current); // 멘트 변경 중단
            setStatus('completed');
        }).catch((e) => {
            // AI 생성 실패(503, 서버 AIImageGenerationException) — 종전엔 500 → /notfound 튕김. 사유를 보여주고 입력 화면으로 되돌린다.
            clearInterval(intervalRef.current);
            setStatus('input');
            alert(e?.message || "AI 이벤트 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.");
        })
    };

    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    // 초기화 핸들러 (테스트용)
    const handleReset = () => {
        setEventName('');
        setCoupons(['']);
        setStatus('input');
    };

    return (
        <div className="bg-gray-50 flex items-center justify-center p-4 font-sans">
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl border border-gray-100 overflow-hidden transition-all duration-300">

                {/* === 1. 입력 페이지 (Input State) === */}
                {status === 'input' && (
                    <div className="p-8">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="bg-indigo-100 p-2 rounded-lg">
                                <Sparkles className="w-6 h-6 text-indigo-600" />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-800">AI 이벤트 생성</h1>
                        </div>

                        <p className="text-gray-500 mb-8 text-sm">
                           AI가 프로젝트부터 진입점까지 한번에 생성해드립니다.
                        </p>

                        <div className="space-y-6">
                            {/* 이벤트명 입력 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    이벤트명
                                </label>
                                <input
                                    type="text"
                                    value={eventName}
                                    onChange={(e) => setEventName(e.target.value)}
                                    placeholder="예: 100% 당첨 이벤트"
                                    maxLength={30}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                                />
                            </div>

                            {/* 쿠폰 목록 입력 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    발행할 쿠폰 목록
                                </label>
                                <label className="block text-sm font-medium text-red-500 mb-2">
                                    ※ 쿠폰을 하나만 만들면 바로 당첨 이벤트가 됩니다.
                                </label>
                                {/* 서버(AIGenerateService.generateProject)는 쿠폰 입력 순서를 등수로 읽어 1등부터
                                    기본 당첨 확률을 매긴다(1등이 가장 낮음). 숫자 표는 서버 한 곳에만 둔다. */}
                                <label className="block text-xs text-gray-500 mb-2">
                                    위에서부터 1등 → 마지막 등수 순서입니다. 당첨 확률은 등수별 기본값(1등이 가장 낮음)으로
                                    자동 세팅되며, 생성 후 이벤트 설정에서 바꿀 수 있습니다.
                                </label>
                                <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                                    {coupons.map((coupon, index) => (
                                        <div key={index} className="flex gap-2 items-center">
                                            <span className="w-9 shrink-0 text-xs font-semibold text-gray-500 text-right">
                                                {index + 1}등
                                            </span>
                                            <input
                                                type="text"
                                                value={coupon}
                                                onChange={(e) => handleCouponChange(index, e.target.value)}
                                                placeholder={`쿠폰명 입력 (예: 10% 할인)`}
                                                maxLength={30}
                                                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all text-sm"
                                            />
                                            {coupons.length > 2 && (
                                                <button
                                                    onClick={() => removeCouponField(index)}
                                                    className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={addCouponField}
                                    className="mt-3 flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                >
                                    <Plus className="w-4 h-4" />
                                    쿠폰 추가하기
                                </button>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    생성 요구사항(선택)
                                </label>
                                <input
                                    type="text"
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    placeholder="예: 한식 프랜차이즈, 밝은 분위기"
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                                />
                            </div>
                        </div>

                        <div className="mt-10">
                            <button
                                onClick={handleGenerate}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-indigo-200 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                            >
                                <Sparkles className="w-5 h-5" />
                                AI 자동 생성 시작하기
                            </button>
                        </div>
                    </div>
                )}

                {/* === 2. 처리중 페이지 (Loading State) === */}
                {status === 'processing' && (
                    <div className="p-12 flex flex-col items-center justify-center min-h-[500px] text-center">
                        <div className="relative mb-8">
                            <div className="absolute inset-0 bg-indigo-200 rounded-full blur-xl opacity-50 animate-pulse"></div>
                            <Loader2 className="w-16 h-16 text-indigo-600 animate-spin relative z-10" />
                        </div>

                        <h2 className="text-xl font-bold text-gray-800 mb-2">
                            AI가 생성 중입니다. 이 페이지를 나가도 좋습니다.
                        </h2>
                        <h2 className="text-xl font-bold text-gray-800 mb-2">
                            쿠폰 개수에 따라 최소 5분이상 소요될 수 있습니다.
                        </h2>
                        <p className="text-gray-500 animate-pulse">
                            {loadingMessage}
                        </p>
                    </div>
                )}

                {/* === 3. 완료 페이지 (Success State) === */}
                {status === 'completed' && (
                    <div className="p-12 flex flex-col items-center justify-center min-h-[500px] text-center">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 animate-bounce-short">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>

                        <h2 className="text-2xl font-bold text-gray-800 mb-4">
                            완료되었습니다!
                        </h2>
                        <p className="text-gray-600 mb-8 leading-relaxed">
                            모든 설정이 성공적으로 끝났습니다.<br />
                            <span className="font-semibold text-gray-900">프로젝트 관리 페이지</span>에서 확인하실 수 있습니다.
                        </p>

                        <div className="w-full space-y-3">
                            <button
                                className="w-full bg-gray-900 hover:bg-black text-white py-3.5 rounded-xl font-medium transition-all"
                                onClick={() => window.location.href = "/project"}
                            >
                                프로젝트 확인하기
                            </button>
                            <button
                                onClick={handleReset}
                                className="w-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 py-3.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                새로 만들기
                            </button>
                        </div>
                    </div>
                )}

            </div>

            {/* Tailwind Custom Animation (style 태그로 추가하거나 tailwind.config.js에 추가) */}
            <style>{`
        @keyframes bounce-short {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-short {
          animation: bounce-short 0.5s ease-in-out;
        }
      `}</style>
        </div>
    );
};

export default AIAutoSetupPage;